
import _ from 'lodash'
import { mapGetters } from 'vuex'
import surveyForm from '~/mixins/survey-form'
import visibilityTracker from '~/mixins/visibility-tracker'
import { progressLabel, progressPercent } from '~/util/quote-progress'

// Filters that describe our own book of business (is this plan commissionable,
// enrollable, contractable) or our data coverage (unscraped) rather than
// anything a beneficiary should weigh. Agents only -- hidden from the panel,
// never seeded from the URL for a client, and stripped from the query string
// they'd otherwise be written back into.
const AGENT_ONLY_FILTERS = ['commissionable_only', 'enrollable_only', 'contractable_only', 'show_unscraped']

export default {
    mixins: [surveyForm, visibilityTracker],
    data () {
        const agentOnly = v => this.$store.getters.canAgent && v === 'true'
        let supported = this.$route.query.supported !== 'false'
        if ((this.$route.query.supported === '' || this.$route.query.supported == null) && this.$store.getters.canAgent) {
            supported = false
        }
        return {
            // quote loads in flight; this page's spinner covers them (#735)
            quotesLoading: 0,
            sortBy: this.$route.query.sortBy || 'highest__score',
            filtersVisible: this.$store.getters.isSize('gte-lg'),
            filters: {
                query: '',
                my_plans: this.$route.query.my_plans === 'true',
                supported,
                // Agent-only (see the template): a client gets false regardless of
                // what the URL says, so a shared or hand-edited link can't turn
                // an internal filter on for them.
                commissionable_only: agentOnly(this.$route.query.commissionable_only),
                enrollable_only: agentOnly(this.$route.query.enrollable_only),
                contractable_only: agentOnly(this.$route.query.contractable_only),
                show_unscraped: agentOnly(this.$route.query.show_unscraped),
                // light: this.$route.query.light !== 'false',
                type: parseInt(this.$route.query.type) || 0,
                benefits: [].concat(this.$route.query.benefits || []),
                carriers: [].concat(this.$route.query.carriers || []),
                mapdTypes: [].concat(this.$route.query.mapdTypes || []),
                suppTypes: [].concat(this.$route.query.suppTypes || []),
            },
            types: [
                { value: 0, text: 'Supplemental (SUPP)', shortcode: 'supp' },
                { value: 1, text: 'Drug (PDP)', shortcode: 'pdp' },
                { value: 2, text: 'Advantage (MA(PD))', shortcode: 'mapd' },
                { value: 3, text: 'Employer/Group (GP)', shortcode: 'gp' },
            ],
            sortByOptions: [
                { value: 'highest__score', text: 'Highest Score' },
                { value: 'lowest__estimated_monthy_cost', text: 'Lowest Estimated Cost Per Month' },
                { value: 'lowest__estimated_yearly_cost', text: 'Lowest Estimated Cost Per Year' },
                { value: 'lowest__effective_date_estimated_yearly_cost', text: 'Lowest Estimated Cost This Year' },
                { value: 'lowest__monthly_premium', text: 'Lowest Premium' },
                { value: 'highest__stars_rating.OVERALL', text: 'Highest Rating' },
                // { value: 'lowest__copays_and_monthly', text: 'Lowest Copays+Premium Cost' },
                // { value: 'highest__copays_and_monthly', text: 'Highest Copays+Premium Cost' },
                { value: 'lowest__score', text: 'Lowest Score' },
                { value: 'highest__estimated_monthy_cost', text: 'Highest Estimated Cost Per Month' },
                { value: 'highest__estimated_yearly_cost', text: 'Highest Estimated Cost Per Year' },
                { value: 'highest__effective_date_estimated_yearly_cost', text: 'Lowest Estimated Cost This Year' },
                { value: 'highest__monthly_premium', text: 'Highest Premium' },
                { value: 'lowest__stars_rating.OVERALL', text: 'Lowest Rating' },
            ],
            benefits: [
                { value: 'has_any_vision', text: 'Has vision' },
                { value: 'has_any_hearing', text: 'Has hearing' },
                { value: 'has_any_fitness', text: 'Has fitness' },
                { value: 'has_any_basic_dental', text: 'Has basic dental' },
                // { value: 'has_any_out_of_network_coverage', text: 'Has out of network coverage' },
                { value: 'has_any_comprehensive_dental', text: 'Has comprehensive dental' },
                { value: 'referral_not_required', text: 'No referral required' },
            ],
        }
    },
    async fetch () {
        await this.saveChanges()
        await this.$store.dispatch('getSurvey', { id: this.uid })
        const [qres] = await Promise.all([
            this.loadQuotes({
                detailed: this.$route.query.detailed === 'true',
                supported: this.filters.supported,
                commissionable_only: this.$route.query.commissionable_only === 'true',
                enrollable_only: this.$route.query.enrollable_only === 'true',
                contractable_only: this.$route.query.contractable_only === 'true',
                // light: this.$route.query.light !== 'false',
            }),
            this.$store.dispatch('getSummary', { id: this.yearSummaryId }),
            this.$store.dispatch('getOpportunities', this.uid),
            this.$store.getters.canAgent && this.$store.getters.inGroup ? this.$store.dispatch('loadAllClientUserGroupPlans') : true,
            this.$store.getters.canAgent ? this.$store.dispatch('loadAepCurrentPlans', { id: this.uid, year: this.$store.getters.year }) : true
        ])
        if (qres?.error?.response?.status === 422 && this.header && this.header.presentQuickEditModal) {
            this.header.presentQuickEditModal()
        }
    },
    computed: {
        ...mapGetters(['year', 'groupId', 'plans', 'userGroupPlans', 'sources', 'client', 'survey', 'quoteProgress', 'yearSummaryId', 'isPlanInSummary', 'quotesLight', 'unscrapedPlans']),
        quotesPending () {
            return this.$fetchState.pending || this.quotesLoading > 0
        },
        quoteProgressLabel () {
            return this.quoteProgress ? progressLabel(this.quoteProgress) : null
        },
        quoteProgressPercent () {
            return this.quoteProgress ? progressPercent(this.quoteProgress) : null
        },
        plansWithUnscraped () {
            // Unscraped plans stay hidden until the agent opts in via "Show Unscraped".
            return this.filters.show_unscraped ? [...this.plans, ...this.unscrapedPlans] : this.plans
        },
        supps () {
            return this.plansWithUnscraped.filter(plan => plan.plan_type === 'supp')
        },
        suppsFiltered () {
            return this.supps.filter(this.plansFilter)
        },
        suppsFilteredSorted () {
            const plans = this.suppsFiltered
            plans.sort(this.plansSort)
            return plans
        },
        // todo: needs to split ma and mapd
        mapds () {
            return this.plansWithUnscraped.filter(plan => plan.plan_type === 'mapd' || plan.plan_type === 'ma')
        },
        mapdsFiltered () {
            return this.mapds.filter(this.plansFilter)
        },
        mapdsFilteredSorted  () {
            const plans = this.mapdsFiltered
            plans.sort(this.plansSort)
            return plans
        },
        pdps () {
            return this.plansWithUnscraped.filter(plan => plan.plan_type === 'pdp')
        },
        pdpsFiltered () {
            return this.pdps.filter(this.plansFilter)
        },
        pdpsFilteredSorted () {
            const plans = this.pdpsFiltered
            plans.sort(this.plansSort)
            return plans
        },
        gps () {
            return this.userGroupPlans.filter(plan => plan.plan_type === 'gp')
        },
        gpsFiltered () {
            return this.gps.filter(this.plansFilter)
        },
        gpsFilteredSorted () {
            const plans = this.gpsFiltered
            plans.sort(this.plansSort)
            return plans
        },
        plansFilteredSorted () {
            const plans = this[`${this.types[this.filters.type]?.shortcode}sFiltered`] || []
            plans.sort(this.plansSort)
            return plans
        },
        sourcesFailed () {
            return Object.keys(this.sources).filter(source => this.sources[source] === false || this.sources[source].success === false)
        },
        sourcesErrors () {
            return Object.keys(this.sources).filter(source => this.sources[source] === false || this.sources[source].success === false).map(source => ({ source, error: this.sources[source].error_message }))
        },
        carriers () {
            const carriers = _.uniqBy(this.plans.map(plan => ({ value: plan.carrier, text: plan.carrier })), c => c.value)
            carriers.sort((c1, c2) => (c1.text > c2.text ? 1 : -1))
            return carriers
        },
        mapdTypes () {
            const mapdTypes = this.mapds.filter(p => p.mapd_type).map(plan => ({ value: plan.mapd_type, text: plan.mapd_type }))
            return _.uniqBy(mapdTypes, pot => pot.value)
        },
        suppTypes () {
            const suppTypes = this.supps.map(plan => ({ value: plan.supp_type, text: plan.supp_type }))
            return _.uniqBy(suppTypes, pot => pot.value)
        },
        plansFilter () {
            const query = this.filters.query
                ? (plan) => {
                    const terms = this.filters.query.split(' ')
                    for (const term of terms) {
                        const termRe = new RegExp(term, 'i')
                        const termMatched = termRe.test(plan.name) || termRe.test(plan.carrier) || (plan.organizations && plan.organizations.some(org => termRe.test(org)))
                        if (!termMatched) return false
                    }
                    return true
                }
                : () => true

            const myPlans = this.filters.my_plans === true ? plan => this.isPlanInSummary(plan) : () => true

            // supported is both recs_only=true and plan_type=gp; unscraped plans are
            // surfaced explicitly via "Show Unscraped", so they bypass this filter.
            const supported = this.filters.supported === true ? plan => plan.plan_type === 'gp' || plan.supported || plan.unscraped : () => true

            const commissionable = this.filters.commissionable_only === true ? plan => this.$store.getters.isPlanCommissionable(plan) : () => true
            const enrollable = this.filters.enrollable_only === true ? plan => this.$store.getters.isPlanEnrollable(plan) : () => true
            const contractable = this.filters.contractable_only === true ? plan => this.$store.getters.isPlanContractable(plan) : () => true

            const benefits = this.filters.benefits && this.filters.benefits.length ? plan => this.filters.benefits.every(b => plan[b]) : () => true
            const carriers = this.filters.carriers && this.filters.carriers.length
                ? plan => this.filters.carriers.some((c) => {
                    const re = new RegExp(c, 'i')
                    return c === plan.carrier || re.test(plan.carrier) || re.test(plan.parent_org) || (plan.organizations && plan.organizations.some(org => re.test(org)))
                })
                : () => true
            const mapdTypes = this.filters.mapdTypes && this.filters.mapdTypes.length ? plan => this.filters.mapdTypes.some(pot => (plan.type === 'mapd' || plan.type === 'ma') ? plan.mapd_type === pot : true) : () => true
            const suppTypes = this.filters.suppTypes && this.filters.suppTypes.length ? plan => this.filters.suppTypes.some(c => plan.type === 'supp' ? (_.toLower(plan.supp_type) === _.toLower(c)) : true) : () => true
            return plan => query(plan) && myPlans(plan) && supported(plan) && commissionable(plan) && enrollable(plan) && contractable(plan) && benefits(plan) && carriers(plan) && mapdTypes(plan) && suppTypes(plan)
        },
        plansSort () {
            const parts = this.sortBy.split('__')
            const orderA = parts[0] === 'highest' ? -1 : 1
            const orderB = orderA === 1 ? -1 : 1
            const property = parts[1]
            return (pA, pB) => (_.get(pA, property) >= _.get(pB, property) ? orderA : orderB)
        },
    },
    watch: {
        year () {
            return this.$fetch()
        },
        sortBy (sortBy) {
            this.$routerx.replaceQuery({ sortBy })
        },
        filters: {
            deep: true,
            handler (filters) {
                this.$nextTick(() => {
                    this.$routerx.replaceQuery(this.shareableFilters(filters))
                })
            },
        },
        'filters.supported' (newValue) {
            return this.loadQuotes({ supported: newValue }, { resetFilters: true })
        },
        'filters.commissionable_only' (newValue) {
            return this.loadQuotes({ commissionable_only: newValue }, { resetFilters: true })
        },
        'filters.enrollable_only' (newValue) {
            return this.loadQuotes({ enrollable_only: newValue }, { resetFilters: true })
        },
        'filters.contractable_only' (newValue) {
            return this.loadQuotes({ contractable_only: newValue }, { resetFilters: true })
        },
        // 'filters.light' (newValue) {
        //     return this.loadQuotes({ light: newValue }, { resetFilters: true })
        // },
    },
    mounted () {
        // $routerx.replaceQuery MERGES into the existing query (plugins/routerx.js)
        // -- it never drops keys -- so omitting the agent-only filters from what
        // we write is not enough on its own: any already present, from a shared
        // or hand-edited link, would just persist. Strip them once on mount.
        if (!this.canAgent) {
            this.$routerx.deleteQuery(AGENT_ONLY_FILTERS)
        }
    },
    methods: {
        // What may be written into the URL. For an agent that's everything; for
        // a client the internal filters are dropped, so their address bar (and
        // any link they share or we log) never carries them.
        shareableFilters (filters) {
            if (this.canAgent) { return filters }
            return _.omit(filters, AGENT_ONLY_FILTERS)
        },
        cardKey (plan, type) {
            // Unscraped plans have id === null; use their pre-stamped unique key instead.
            return plan.unscraped ? plan.key : `${type}-${plan.id}`
        },
        loadQuotes (params = {}, options = {}) {
            // this page's spinner, not the full-screen one: two spinners at once
            // otherwise (#735)
            this.quotesLoading++
            const p = this.$store.dispatch('getQuotes', {
                id: this.uid,
                fresh: false,
                spinner: false,
                progress: true,
                detailed: this.$route.query.detailed === 'true',
                supported: this.filters.supported,
                commissionable_only: this.$route.query.commissionable_only === 'true',
                enrollable_only: this.$route.query.enrollable_only === 'true',
                contractable_only: this.$route.query.contractable_only === 'true',
                // light: this.$route.query.light !== 'false',
                ...params
            }).finally(() => {
                this.quotesLoading--
            })
            if (options.resetFilters) {
                // reset filters since suppTypes and carriers are different between v1 and v2
                this.filters.suppTypes = []
                this.filters.mapdTypes = []
                this.filters.benefits = []
                this.filters.carriers = []
                this.filters.query = ''
                this.filters.my_plans = false
                this.filters.type = 0
            }
            return p
        },
    }
}
